import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  CheckCircle,
  Euro,
  Person,
  Email,
  CalendarToday,
  Receipt
} from '@mui/icons-material';
import { notificationService } from '../services/NotificationService';
import { useAuth } from '../contexts/AuthContext';

interface SessionData {
  id: string;
  status: string;
  amount_total: number;
  currency: string;
  customer_email: string;
  client_reference_id: string;
  metadata: any;
  created: number;
}

const PaymentSuccessScreen: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sessionData, setSessionData] = useState<SessionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const sessionId = searchParams.get('session_id');
  const bookingId = searchParams.get('booking');

  useEffect(() => {
    const fetchSessionData = async () => {
      if (!sessionId) {
        setError('Aucun ID de session trouvé');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`http://localhost:3001/api/stripe/session/${sessionId}`);
        const data = await response.json();

        if (data.success) {
          setSessionData(data.session);
          
          // Créer la réservation dans Firestore via l'API
          if (user && data.session?.metadata) {
            try {
              const { bookingService } = await import('../services/bookingService');
              
              const bookingResult = await bookingService.createBooking({
                dressId: data.session.metadata.dressId || '',
                ownerId: data.session.metadata.ownerId || '',
                renterId: user.uid,
                startDate: data.session.metadata.startDate || new Date().toISOString(),
                endDate: data.session.metadata.endDate || new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString(),
                totalAmount: (data.session.amount_total || 0) / 100,
                deposit: parseFloat(data.session.metadata.deposit || '0'),
                deliveryMethod: data.session.metadata.deliveryMethod || 'pickup',
                specialRequests: data.session.metadata.specialRequests || '',
                checkoutSessionId: data.session.id,
                customerEmail: data.session.customer_email || user.email || '',
                dressTitle: data.session.metadata.productName || data.session.metadata.dressTitle || 'Robe',
                dressImages: data.session.metadata.imageUrl ? [data.session.metadata.imageUrl] : []
              });

              if (bookingResult.success && bookingResult.booking) {
                console.log('✅ Réservation créée avec succès:', bookingResult.booking.id);
                
                // Envoyer une notification de paiement réussi
                try {
                  await notificationService.notifyPaymentSuccess(
                    user.uid,
                    data.session.metadata?.productName || 'Robe',
                    data.session.amount_total / 100,
                    bookingResult.booking.id
                  );
                } catch (notificationError) {
                  console.error('Erreur lors de l\'envoi de la notification:', notificationError);
                }
              } else {
                console.error('❌ Erreur création réservation:', bookingResult.error);
                // Fallback: sauvegarder dans localStorage si l'API échoue
                try {
                  const bookingsRaw = localStorage.getItem('myRentals');
                  const bookings = bookingsRaw ? JSON.parse(bookingsRaw) : [];
                  const nowIso = new Date().toISOString();
                  bookings.unshift({
                    id: `bkg_${Date.now()}`,
                    sessionId: data.session.id,
                    title: data.session?.metadata?.productName || 'Location',
                    priceCents: data.session?.metadata?.originalPrice ?? data.session.amount_total,
                    totalCents: data.session.amount_total,
                    currency: data.session.currency,
                    email: data.session.customer_email,
                    bookedAt: nowIso,
                    startDate: data.session?.metadata?.startDate || nowIso,
                    endDate: data.session?.metadata?.endDate || new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString(),
                    imageUrl: data.session?.metadata?.imageUrl || data.session?.metadata?.image || '',
                    dressId: data.session?.metadata?.dressId || '',
                    ownerId: data.session?.metadata?.ownerId || ''
                  });
                  localStorage.setItem('myRentals', JSON.stringify(bookings));
                } catch (_e) {
                  // no-op localStorage
                }
              }
            } catch (bookingError) {
              console.error('❌ Erreur lors de la création de la réservation:', bookingError);
              // Fallback: sauvegarder dans localStorage
              try {
                const bookingsRaw = localStorage.getItem('myRentals');
                const bookings = bookingsRaw ? JSON.parse(bookingsRaw) : [];
                const nowIso = new Date().toISOString();
                bookings.unshift({
                  id: `bkg_${Date.now()}`,
                  sessionId: data.session.id,
                  title: data.session?.metadata?.productName || 'Location',
                  priceCents: data.session?.metadata?.originalPrice ?? data.session.amount_total,
                  totalCents: data.session.amount_total,
                  currency: data.session.currency,
                  email: data.session.customer_email,
                  bookedAt: nowIso,
                  startDate: data.session?.metadata?.startDate || nowIso,
                  endDate: data.session?.metadata?.endDate || new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString(),
                  imageUrl: data.session?.metadata?.imageUrl || data.session?.metadata?.image || '',
                  dressId: data.session?.metadata?.dressId || '',
                  ownerId: data.session?.metadata?.ownerId || ''
                });
                localStorage.setItem('myRentals', JSON.stringify(bookings));
              } catch (_e) {
                // no-op localStorage
              }
            }
          }
        } else {
          setError(data.error || 'Erreur lors de la récupération de la session');
        }
      } catch (err) {
        setError('Erreur de connexion au serveur');
      } finally {
        setLoading(false);
      }
    };

    fetchSessionData();
  }, [sessionId]);

  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(amount / 100);
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Vérification du paiement...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box maxWidth="600px" mx="auto" p={3}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={() => navigate('/')}>
          Retour à l'accueil
        </Button>
      </Box>
    );
  }

  if (!sessionData) {
    return (
      <Box maxWidth="600px" mx="auto" p={3}>
        <Alert severity="warning" sx={{ mb: 3 }}>
          Aucune donnée de session disponible
        </Alert>
        <Button variant="contained" onClick={() => navigate('/')}>
          Retour à l'accueil
        </Button>
      </Box>
    );
  }

  return (
    <Box maxWidth="800px" mx="auto" p={3}>
      {/* En-tête de succès */}
      <Box textAlign="center" mb={4}>
        <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
        <Typography variant="h4" gutterBottom color="success.main">
          Paiement Réussi !
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Votre réservation a été confirmée
        </Typography>
      </Box>

      {/* Carte de confirmation */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Confirmation de Paiement
            </Typography>
            <Chip 
              label={sessionData.status === 'paid' ? 'Payé' : sessionData.status}
              color={sessionData.status === 'paid' ? 'success' : 'default'}
              icon={<Receipt />}
            />
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Détails de la session */}
          <List>
            <ListItem>
              <ListItemIcon>
                <Receipt />
              </ListItemIcon>
              <ListItemText
                primary="ID de Session"
                secondary={sessionData.id}
              />
            </ListItem>

            <ListItem>
              <ListItemIcon>
                <Euro />
              </ListItemIcon>
              <ListItemText
                primary="Montant"
                secondary={formatAmount(sessionData.amount_total, sessionData.currency)}
              />
            </ListItem>

            <ListItem>
              <ListItemIcon>
                <Person />
              </ListItemIcon>
              <ListItemText
                primary="Réservation"
                secondary={sessionData.client_reference_id || 'N/A'}
              />
            </ListItem>

            <ListItem>
              <ListItemIcon>
                <Email />
              </ListItemIcon>
              <ListItemText
                primary="Email"
                secondary={sessionData.customer_email || 'N/A'}
              />
            </ListItem>

            <ListItem>
              <ListItemIcon>
                <CalendarToday />
              </ListItemIcon>
              <ListItemText
                primary="Date de paiement"
                secondary={formatDate(sessionData.created)}
              />
            </ListItem>
          </List>

          {/* Détails techniques masqués volontairement */}
        </CardContent>
      </Card>

      {/* Actions */}
      <Box display="flex" gap={2} justifyContent="center">
        <Button 
          variant="contained" 
          onClick={() => navigate('/dashboard')}
          sx={{ backgroundColor: '#a5816d', '&:hover': { backgroundColor: '#8B7355' } }}
        >
          Voir mes réservations
        </Button>
        <Button 
          variant="outlined" 
          onClick={() => navigate('/')}
        >
          Retour à l'accueil
        </Button>
      </Box>
    </Box>
  );
};

export default PaymentSuccessScreen;
